// src/logic/loginLogic.js
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { app } from "../firebase"; // already initialized
import { requestNotificationPermission, getFCMToken, saveFCMToken, savePendingFCMToken } from '../utils/notificationManager';
import { logAuthEvent, logSecurityEvent, EVENT_TYPES } from '../utils/securityLogger';
import { refreshSession } from '../utils/sessionManager';
import { checkRateLimit, recordAttempt, RATE_LIMIT_CONFIG } from '../utils/rateLimiter';
import { validateEmail, detectDangerousPatterns, sanitizeInput } from '../utils/inputValidator';


export const loginUser = async (email, password) => {
  try {
    // Validate and sanitize inputs
    const emailValidation = await validateEmail(email);
    if (!emailValidation.isValid) {
      return { 
        success: false, 
        message: 'Invalid email format',
        validationErrors: emailValidation.errors
      };
    }

    // Note: Password validation is not applied during login
    // Password validation is only used during registration, password change, and password reset

    // Check for dangerous patterns
    const dangerousPatterns = detectDangerousPatterns(email + ' ' + password);
    if (dangerousPatterns.length > 0) {
      await logSecurityEvent(EVENT_TYPES.INJECTION_ATTEMPT, null, {
        patterns: dangerousPatterns,
        input: 'login_credentials',
        email: email.substring(0, 10) + '...'
      });
      
      return { 
        success: false, 
        message: 'Invalid input detected'
      };
    }

    // Use sanitized inputs
    const sanitizedEmail = emailValidation.sanitized;
    // Password is not validated during login, only sanitized for security
    const sanitizedPassword = sanitizeInput(password, { removeHtml: true, escapeHtml: true });

    // Check rate limit before attempting login
    const rateLimitCheck = await checkRateLimit('login', sanitizedEmail, RATE_LIMIT_CONFIG.LOGIN_ATTEMPTS);
    
    if (rateLimitCheck.isLimited) {
      // Log brute force attempt
      await logSecurityEvent(EVENT_TYPES.BRUTE_FORCE_ATTEMPT, null, {
        email: email,
        remainingTime: rateLimitCheck.remainingTime,
        reason: rateLimitCheck.reason
      });
      
      return { 
        success: false, 
        message: `Too many login attempts. Please try again in ${rateLimitCheck.remainingTime} seconds.`,
        rateLimited: true,
        remainingTime: rateLimitCheck.remainingTime
      };
    }

    // Record login attempt
    await recordAttempt('login', sanitizedEmail, RATE_LIMIT_CONFIG.LOGIN_ATTEMPTS);

    const userCredential = await signInWithEmailAndPassword(auth, sanitizedEmail, sanitizedPassword);
    const firebaseUser = userCredential.user;

    // 🔐 Load user data (e.g. pseudo, DP)
    const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
    const userData = userDoc.exists() ? userDoc.data() : {};

    // Log successful login (non-blocking)
    logAuthEvent(EVENT_TYPES.LOGIN_SUCCESS, firebaseUser.uid, {
      email: firebaseUser.email,
      pseudo: userData.pseudo || 'Duelist',
      hasTOTP: userData.totpEnabled || false,
      loginMethod: 'email_password'
    }).catch((err) => console.warn('⚠️ Failed to log LOGIN_SUCCESS:', err?.message || err));

    // Log session start (non-blocking)
    logAuthEvent(EVENT_TYPES.SESSION_STARTED, firebaseUser.uid, {
      email: firebaseUser.email,
      loginMethod: 'email_password'
    }).catch((err) => console.warn('⚠️ Failed to log SESSION_STARTED:', err?.message || err));

    // Refresh session to ensure proper tracking
    refreshSession();

    // Check if user has TOTP enabled
    if (userData.totpEnabled && userData.totpSecret) {
      // Return user data for TOTP verification instead of completing login
      return { 
        success: false, 
        requiresTOTP: true, 
        user: {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          pseudo: userData.pseudo || 'Duelist',
          totpSecret: userData.totpSecret,
          totpBackupCodes: userData.totpBackupCodes || []
        }
      };
    }

    // Continue with normal login if TOTP is not enabled
    return await completeLogin(firebaseUser, userData, password);
  } catch (error) {
    console.error('Login error:', error);
    
    // Log failed login attempt
    await logAuthEvent(EVENT_TYPES.LOGIN_FAILED, null, {
      email: email,
      errorCode: error.code,
      errorMessage: error.message,
      loginMethod: 'email_password'
    });

    // Check for suspicious activity (multiple failed attempts)
    await checkForSuspiciousActivity(email);
    
    return { success: false, message: 'Invalid email or password' };
  }
};

export const completeLogin = async (firebaseUser, userData, password) => {
  try {
    const session = {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      pseudo: userData.pseudo || 'Duelist',
    };

    // Persist session
    localStorage.setItem('userSession', JSON.stringify(session));
    // ✅ Store admin password (for re-auth in create-user) only when provided
    if (password) {
      localStorage.setItem('adminPassword', password);
    }

    // Notify app that session changed (lets App.js react immediately)
    try {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('dp-duel-session-changed'));
      }
    } catch {
      // ignore
    }

    // Run slow post-login tasks in the background (do NOT block navigation)
    (async () => {
      try {
        // 🔔 Setup notifications
        const hasPermission = await requestNotificationPermission();
        if (hasPermission) {
          const fcmToken = await getFCMToken();
          if (fcmToken) {
            await saveFCMToken(firebaseUser.uid, fcmToken);
          }
        }

        // Save any pending FCM token stored before login
        await savePendingFCMToken(firebaseUser.uid);
      } catch (notifError) {
        console.warn('⚠️ Post-login notification setup failed:', notifError?.message || notifError);
      }

      // ✅ Mark user as logged in (FireStore) - non-blocking
      try {
        const { doc, updateDoc } = await import('firebase/firestore');
        const { db } = await import('../firebase');

        const userRef = doc(db, 'users', firebaseUser.uid);
        await updateDoc(userRef, {
          isLoggedIn: true,
          lastLoginTime: new Date()
        });

        console.log('✅ User login status updated successfully');
      } catch (updateError) {
        console.warn('⚠️ Failed to update user login status:', updateError?.message || updateError);
      }
    })();

    return { success: true, session };
  } catch (error) {
    console.error('Complete login error:', error);
    return { success: false, message: 'Login completion failed' };
  }
};

export const verifyTOTPAndCompleteLogin = async (user, totpToken) => {
  try {
    const { verifyTOTPToken } = await import('../utils/totpUtils');
    
    // Verify TOTP token
    const isValidToken = await verifyTOTPToken(totpToken, user.totpSecret);
    
    if (!isValidToken) {
      // Log failed TOTP verification
      await logAuthEvent(EVENT_TYPES.TOTP_VERIFICATION_FAILED, user.uid, {
        totpToken: totpToken.substring(0, 2) + '****', // Masked for security
        error: 'Invalid verification code'
      });
      
      return { success: false, message: 'Invalid verification code' };
    }

    // Log successful TOTP verification
    await logAuthEvent(EVENT_TYPES.TOTP_VERIFICATION_SUCCESS, user.uid, {
      totpToken: totpToken.substring(0, 2) + '****', // Masked for security
      verificationMethod: 'TOTP'
    });

    // Complete the login process
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    const userData = userDoc.exists() ? userDoc.data() : {};
    
    return await completeLogin(
      { uid: user.uid, email: user.email },
      userData,
      '' // Password not needed for TOTP verification
    );
  } catch (error) {
    console.error('TOTP verification error:', error);
    
    // Log TOTP verification error
    await logAuthEvent(EVENT_TYPES.TOTP_VERIFICATION_FAILED, user.uid, {
      error: error.message,
      errorCode: error.code
    });
    
    return { success: false, message: 'Verification failed' };
  }
};

export const verifyBackupCodeAndCompleteLogin = async (user, backupCode) => {
  try {
    // Check if backup code exists in user's backup codes
    const isValidBackupCode = user.totpBackupCodes && 
      user.totpBackupCodes.includes(backupCode.toUpperCase());
    
    if (!isValidBackupCode) {
      return { success: false, message: 'Invalid backup code' };
    }

    // Remove used backup code
    const updatedBackupCodes = user.totpBackupCodes.filter(code => code !== backupCode.toUpperCase());
    const { updateUserProfileWithBackup } = await import('../utils/clientBackupWrapper');
    await updateUserProfileWithBackup(user.uid, {
      totpBackupCodes: updatedBackupCodes
    }, 'security_update', {
      field: 'backup_code_used',
      remainingCodes: updatedBackupCodes.length,
      usedCode: backupCode.toUpperCase()
    });

    // Complete the login process
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    const userData = userDoc.exists() ? userDoc.data() : {};
    
    return await completeLogin(
      { uid: user.uid, email: user.email },
      userData,
      '' // Password not needed for backup code verification
    );
  } catch (error) {
    console.error('Backup code verification error:', error);
    return { success: false, message: 'Backup code verification failed' };
  }
};

export const getLoggedInUser = () => {
  const session = localStorage.getItem('userSession');
  return session ? JSON.parse(session) : null;
};

export const logoutUser = async () => {
  try {
    // Clear FCM token from Firestore to stop notifications
    const userSession = localStorage.getItem('userSession');
    if (userSession) {
      const user = JSON.parse(userSession);
      if (user && user.uid) {
        const { updateUserProfileWithBackup } = await import('../utils/clientBackupWrapper');
        await updateUserProfileWithBackup(user.uid, {
          fcmToken: null,
          lastTokenUpdate: null,
          isLoggedIn: false,
          lastLogoutTime: new Date()
        }, 'auth_update', {
          field: 'logout',
          logoutTime: new Date().toISOString()
        });
        console.log('✅ FCM token cleared on logout for user:', user.uid);
      }
    }
  } catch (error) {
    console.error('❌ Error clearing FCM token on logout:', error);
  }
  
  // Get current user before logout
  const currentUser = getLoggedInUser();
  
  // Log logout event
  if (currentUser) {
    await logAuthEvent(EVENT_TYPES.LOGOUT, currentUser.uid, {
      logoutMethod: 'manual',
      sessionDuration: Date.now() - (currentUser.loginTime || Date.now())
    });
  }
  
  // Clear local session
  localStorage.removeItem('userSession');
  localStorage.removeItem('adminPassword');
  localStorage.removeItem('pendingFCMToken');
};

export const resetPassword = async (email) => {
  const auth = getAuth(app);
  try {
    // Validate and sanitize email input
    const emailValidation = await validateEmail(email);
    if (!emailValidation.isValid) {
      return { 
        success: false, 
        message: 'Invalid email format',
        validationErrors: emailValidation.errors
      };
    }

    // Check for dangerous patterns
    const dangerousPatterns = detectDangerousPatterns(email);
    if (dangerousPatterns.length > 0) {
      await logSecurityEvent(EVENT_TYPES.INJECTION_ATTEMPT, null, {
        patterns: dangerousPatterns,
        input: 'password_reset_email',
        email: email.substring(0, 10) + '...'
      });
      
      return { 
        success: false, 
        message: 'Invalid input detected'
      };
    }

    const sanitizedEmail = emailValidation.sanitized;

    // Check rate limit before attempting password reset
    const rateLimitCheck = await checkRateLimit('password_reset', sanitizedEmail, RATE_LIMIT_CONFIG.PASSWORD_RESET_ATTEMPTS);
    
    if (rateLimitCheck.isLimited) {
      // Log brute force attempt
      await logSecurityEvent(EVENT_TYPES.BRUTE_FORCE_ATTEMPT, null, {
        email: email,
        remainingTime: rateLimitCheck.remainingTime,
        reason: rateLimitCheck.reason,
        action: 'password_reset'
      });
      
      return { 
        success: false, 
        message: `Too many password reset attempts. Please try again in ${rateLimitCheck.remainingTime} seconds.`,
        rateLimited: true,
        remainingTime: rateLimitCheck.remainingTime
      };
    }

    // Record password reset attempt
    await recordAttempt('password_reset', sanitizedEmail, RATE_LIMIT_CONFIG.PASSWORD_RESET_ATTEMPTS);

    await sendPasswordResetEmail(auth, sanitizedEmail, {
      url: "https://yme-dp-manager.web.app/reset-password",
      handleCodeInApp: true
    });
    
    // Log password reset request
    await logAuthEvent(EVENT_TYPES.PASSWORD_RESET_REQUEST, null, {
      email: email,
      resetMethod: 'email'
    });
    
    return { success: true };
  } catch (error) {
    console.error("Reset password error:", error);
    
    // Log failed password reset attempt
    await logAuthEvent(EVENT_TYPES.PASSWORD_RESET_REQUEST, null, {
      email: email,
      errorCode: error.code,
      errorMessage: error.message,
      success: false
    });
    
    return { success: false, message: error.message };
  }
};

/**
 * Check for suspicious login activity
 */
async function checkForSuspiciousActivity(email) {
  try {
    // In a real implementation, you would check recent failed login attempts
    // from the security logs and implement rate limiting
    const recentFailures = 0; // This would be fetched from logs
    
    if (recentFailures >= 5) {
      await logSecurityEvent(EVENT_TYPES.BRUTE_FORCE_ATTEMPT, null, {
        email: email,
        failureCount: recentFailures,
        action: 'account_locked'
      });
    } else if (recentFailures >= 3) {
      await logSecurityEvent(EVENT_TYPES.SUSPICIOUS_ACTIVITY, null, {
        email: email,
        failureCount: recentFailures,
        action: 'monitoring_increased'
      });
    }
  } catch (error) {
    console.error('Error checking suspicious activity:', error);
  }
}

